import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils/cn';

interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  centerValue?: string | number;
  className?: string;
}

export function DonutChart({
  segments,
  size = 140,
  thickness = 20,
  centerLabel,
  centerValue,
  className,
}: DonutChartProps) {
  const [entered, setEntered] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 80);
    return () => clearTimeout(t);
  }, []);

  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const activeSegments = segments.filter((s) => s.value > 0);
  const GAP = activeSegments.length > 1 ? 3 : 0;

  let cumulativePct = 0;
  const arcs = activeSegments.map((seg, i) => {
    const pct = seg.value / total;
    const dashLen = Math.max(0, pct * circumference - GAP);
    const gapLen = circumference - dashLen;
    const rotation = cumulativePct * 360 - 90;
    cumulativePct += pct;
    return { ...seg, dashLen, gapLen, rotation, arcIdx: i };
  });

  return (
    <div className={cn('flex flex-col items-center gap-5', className)}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
          <defs>
            {arcs.map((arc) => (
              <filter
                key={`df-${arc.arcIdx}`}
                id={`df-${arc.arcIdx}`}
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            ))}
          </defs>

          {/* Background track */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.05)"
            strokeWidth={thickness}
          />

          {/* Segment arcs */}
          {arcs.map((arc) => {
            const isHov = hoveredIdx === arc.arcIdx;
            return (
              <circle
                key={arc.label}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={arc.color}
                strokeWidth={isHov ? thickness + 5 : thickness}
                strokeDasharray={`${arc.dashLen} ${arc.gapLen}`}
                strokeLinecap="butt"
                transform={`rotate(${arc.rotation} ${cx} ${cy})`}
                filter={isHov ? `url(#df-${arc.arcIdx})` : undefined}
                style={{
                  strokeDashoffset: entered ? 0 : arc.dashLen,
                  transition: `stroke-dashoffset 0.65s cubic-bezier(0.4,0,0.2,1) ${arc.arcIdx * 0.1}s, stroke-width 0.2s ease, opacity 0.2s ease`,
                  opacity: hoveredIdx !== null && !isHov ? 0.4 : 1,
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setHoveredIdx(arc.arcIdx)}
                onMouseLeave={() => setHoveredIdx(null)}
              />
            );
          })}
        </svg>

        {/* Center content */}
        {(centerLabel !== undefined || centerValue !== undefined) && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
            {centerValue !== undefined && (
              <span className="text-xl font-bold leading-none text-white">
                {typeof centerValue === 'number' ? centerValue.toLocaleString() : centerValue}
              </span>
            )}
            {centerLabel !== undefined && (
              <span
                className="mt-1 text-[9px] font-semibold uppercase tracking-widest"
                style={{ color: 'rgba(255,255,255,0.35)' }}
              >
                {centerLabel}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex w-full flex-col gap-1.5">
        {activeSegments.map((seg, i) => {
          const pct = total === 0 ? 0 : (seg.value / total) * 100;
          const isHov = hoveredIdx === i;
          return (
            <div
              key={seg.label}
              className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-xs transition-all"
              style={{ background: isHov ? `${seg.color}18` : 'transparent' }}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="h-2 w-2 shrink-0 rounded-full transition-all"
                  style={{
                    background: seg.color,
                    boxShadow: isHov ? `0 0 8px ${seg.color}` : 'none',
                  }}
                />
                <span className="truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {seg.label}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="font-semibold text-white">
                  {seg.value.toLocaleString()}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.28)', fontSize: 10 }}>
                  {pct.toFixed(0)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
